import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import {
  useSelectEngagementRowGroupColsOptions,
  useSelectEngagementSelectedCompanies,
  useSelectEngagementSelectedLocations,
} from 'Deer/intent/Engagement/slice/EngagementIntentSlice';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { MODULE_KEYS_METRIC_CARDS_DEER } from 'Deer/metricCards/constants';
import { AgGridReact } from 'ag-grid-react';
import { useToast } from 'components/hooks/useToast';
import { useLocation } from 'react-router-dom';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { BulkActionsSplitButton } from 'screens/performanceReview/manageAndInsights/ReviewManage/components/ReviewManageTable/components/BulkActions/BulkActionsSplitButton';
import { CellRenderer } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import {
  STATUS_TO_COLOR_MAP,
  STATUS_TO_LABEL_MAP,
} from 'screens/performanceReview/manageAndInsights/constants/StatusValues';

import { RightPoint } from 'assets/assetsV1/iconComponents';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Button } from 'uiToolkitV1/Button';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { CompletionStatusColDefs } from './constants/ColDefs';
import {
  notifyEngagementReminderApi,
  useCompletionStatusMetricCardsDivisionView,
  useCompletionStatusMetricCardsEmployeeView,
  useGetEngagementCompletionStatus,
} from './hooks/dataHooks';

type Props = {
  gridRef: React.RefObject<AgGridReact>;
};

const StatusCellRenderer = ({ value }: { value: string }) => (
  <Box className="info">
    {value && STATUS_TO_LABEL_MAP[value] ? (
      <TagColored color={STATUS_TO_COLOR_MAP[value]}>{STATUS_TO_LABEL_MAP[value]}</TagColored>
    ) : (
      <TextBodyMedium>NA</TextBodyMedium>
    )}
  </Box>
);

const HierarchyCellRenderer = ({ value }: { value: string }) =>
  value?.split(' > ')?.length === 1 ? (
    <TextBodyMedium>{value?.split(' > ')[0]}</TextBodyMedium>
  ) : (
    <Flex alignItems="center">
      {value?.split(' > ')?.map((item, index, arr) => (
        <React.Fragment key={item}>
          <TextBodyMedium>{item}</TextBodyMedium>
          {index < arr.length - 1 && <RightPoint boxSize="20px" color="#666666" />}
        </React.Fragment>
      ))}
    </Flex>
  );

const DIVISION_COLUMNS_TO_HIDE = ['RESPONDED_COUNT', 'PENDING_COUNT'];

const CompletionStatusBulkActionHeader = ({
  clearSelection,
  selectAll,
  totalRows,
  totalRowsSelected,
  actions,
  isViewByDivision,
}) =>
  totalRows > 0 ? (
    <Box display="flex" alignItems="center" mb={3}>
      <Box
        display="flex"
        px="12px"
        py={1}
        alignItems="center"
        bgColor="neutral.F5F7F9"
        borderWidth="1px"
        borderColor="neutral.EBEFF3"
        borderRadius="4px"
        flex={1}
      >
        {totalRows === totalRowsSelected ? (
          <TextBodyMedium bold>
            All {totalRowsSelected > 1 ? `${totalRowsSelected} ` : ''} {isViewByDivision ? 'divisions' : 'employees'}{' '}
            are selected
          </TextBodyMedium>
        ) : (
          totalRowsSelected > 0 && (
            <TextBodyMedium bold>
              Total {totalRowsSelected}{' '}
              {totalRowsSelected > 1
                ? `${isViewByDivision ? 'divisions' : 'employees'} are`
                : `${isViewByDivision ? 'division' : 'employee'} is`}{' '}
              selected
            </TextBodyMedium>
          )
        )}
        {totalRows !== totalRowsSelected && (
          <Button variant="tertiary" onClick={selectAll}>
            Select all {totalRows} {isViewByDivision ? 'divisions' : 'employees'}
          </Button>
        )}
        {totalRowsSelected > 0 && (
          <Button variant="tertiary" onClick={clearSelection}>
            Clear Selection
          </Button>
        )}
        <Box ml="auto">
          <BulkActionsSplitButton options={actions} isDisabled={totalRowsSelected === 0} />
        </Box>
      </Box>
    </Box>
  ) : null;

const CompletionStatus = ({ gridRef }: Props) => {
  const viewByOptions = useSelectEngagementRowGroupColsOptions();

  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const selectedSurvey = searchParams.get('surveyId');

  const viewByURLQueryParam = searchParams.get('viewBy');

  const selectedViewBy = useMemo(
    () => viewByOptions.filter((option) => option.field === viewByURLQueryParam),
    [viewByOptions, viewByURLQueryParam]
  );

  const isViewByDivision = selectedViewBy.some((option) => option.field === 'division');

  const selectedCompaniesIds = useSelectEngagementSelectedCompanies();
  const selectedLocations = useSelectEngagementSelectedLocations();

  const { data, isLoading } = useGetEngagementCompletionStatus({
    reportType: 'ENGAGEMENT_COMPLETION',
    rowGroupCols: selectedViewBy,
    feedbackFormId: Number(selectedSurvey),
    companyId: selectedCompaniesIds.length > 0 ? selectedCompaniesIds : undefined,
    location: selectedLocations.length > 0 ? selectedLocations : undefined,
  });

  const { columns, rows } = data ?? {};

  const { metricsCardsData: metricsDataForDivision, onModelUpdated: onViewByDivisionModelUpdated } =
    useCompletionStatusMetricCardsDivisionView({
      gridRef,
    });

  const { metricsCardsData: metricsDataForEmployee, onModelUpdated: onViewByEmployeeModelUpdated } =
    useCompletionStatusMetricCardsEmployeeView({
      gridRef,
    });

  const columnDefs = useMemo(() => {
    if (isViewByDivision) {
      // filtering out columns that are not relevant for division view
      const filteredColumns = columns?.filter((col) => !DIVISION_COLUMNS_TO_HIDE.includes(col.columnId));
      return (
        filteredColumns?.map((col) => ({
          ...col,
          field: col.columnId,
          headerName: col.displayName,
          colId: col.columnId,
          cellRenderer:
            // eslint-disable-next-line no-nested-ternary
            col.columnId === 'STATUS'
              ? StatusCellRenderer
              : col.columnId === 'DIVISION'
                ? HierarchyCellRenderer
                : CellRenderer,
        })) ?? []
      );
    }

    return (
      columns?.map((col) => ({
        ...col,
        ...(CompletionStatusColDefs[col.columnId] ?? {}),
        field: col.columnId,
        headerName: col.displayName,
        colId: col.columnId,
        cellRenderer:
          // eslint-disable-next-line no-nested-ternary
          col.columnId === 'STATUS'
            ? StatusCellRenderer
            : col.columnId === 'EMPLOYEE_FUNCTION_HIERARCHY'
              ? HierarchyCellRenderer
              : CellRenderer,
        minWidth: 230,
      })) ?? []
    );
  }, [columns, isViewByDivision]);

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      headerComponent: CustomHeaderCell as (props: unknown) => JSX.Element,
      sortable: true,
      minWidth: 230,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'] as const,
    }),
    []
  );

  // Local state for bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    if (rows) {
      setTotalRows(rows.length);
    }
  }, [rows]);

  // AgGrid selection handlers
  const onSelectionChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (api) {
      const selected = api.getSelectedRows();
      if (isViewByDivision) {
        setSelectedIds(selected.map((row) => row?.DIVISION));
      }
      setSelectedIds(selected.map((row) => row?.EMPLOYEE?.id));
    }
  }, [gridRef, isViewByDivision]);

  const handleSelectAll = useCallback(() => {
    gridRef.current?.api?.selectAll?.('filtered');
  }, [gridRef]);

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api?.deselectAll?.();
    setSelectedIds([]);
  }, [gridRef]);

  const toast = useToast();

  // Example bulk actions (customize as needed)
  const actions = [
    {
      value: 'Notify',
      label: 'Notify',
      onClick: async () => {
        try {
          // await notifyEngagementReminderApi({ surveyId: selectedSurvey, employeeIds: selectedIds });
          // wait for sometime
          await new Promise((resolve) => {
            setTimeout(resolve, 450);
          });
          toast({ title: 'Notification sent', status: 'success' });
          handleClearSelection();
        } catch (error) {
          toast({ title: 'Failed to send notification', status: 'error' });
        }
      },
      isDisabled: selectedIds.length === 0,
    },
    // Add more actions as needed
  ];

  return (
    <Box>
      {isViewByDivision && (
        <Box mb={3}>
          <MetricCards
            height="100%"
            columns={`repeat(${metricsDataForDivision.length}, 1fr)`}
            cardTitle="Metrics"
            cards={metricsDataForDivision}
            module={MODULE_KEYS_METRIC_CARDS_DEER.ENGAGEMENT}
            cardStyle="style1"
          />
        </Box>
      )}
      {!isViewByDivision && (
        <Box mb={3}>
          <MetricCards
            height="100%"
            columns={`repeat(${metricsDataForEmployee.length}, 1fr)`}
            cardTitle="Metrics"
            cards={metricsDataForEmployee}
            module={MODULE_KEYS_METRIC_CARDS_DEER.ENGAGEMENT}
            cardStyle="style1"
          />
        </Box>
      )}
      <PaperWithShadow p={4} mb={1}>
        <CompletionStatusBulkActionHeader
          clearSelection={handleClearSelection}
          selectAll={handleSelectAll}
          totalRows={totalRows}
          totalRowsSelected={selectedIds.length}
          actions={actions}
          isViewByDivision={isViewByDivision}
        />

        <div className="ag-theme-alpine engagement-report-grid">
          <AgGridTable
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            domLayout="autoHeight"
            rowData={rows}
            loading={isLoading}
            pagination
            paginationPageSize={10}
            rowHeight={56}
            kebabMenuIsRowSpecific={false}
            containerAutoHeight
            onModelUpdated={isViewByDivision ? onViewByDivisionModelUpdated : onViewByEmployeeModelUpdated}
            rowSelection={{ mode: 'multiRow', enableClickSelection: false }}
            onSelectionChanged={onSelectionChanged}
          />
        </div>
      </PaperWithShadow>
    </Box>
  );
};

export { CompletionStatus };
